// src/contexts/TransactionContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { NetworkContext } from './NetworkContext';
import { WalletContext } from './WalletContext';

// Create the context
export const TransactionContext = createContext(null);

// Transaction types enum
export const TRANSACTION_TYPES = {
  SEND: 'SEND',
  RECEIVE: 'RECEIVE',
  SWAP: 'SWAP',
  APPROVE: 'APPROVE',
  CONTRACT: 'CONTRACT',
  UNKNOWN: 'UNKNOWN'
};

// Storage key for localStorage
const TRANSACTIONS_STORAGE_KEY = 'bitdog_transactions';

export const TransactionProvider = ({ children }) => {
  const { currentChain } = useContext(NetworkContext);
  const { address } = useContext(WalletContext);
  
  // State to store all transactions
  const [transactions, setTransactions] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load transactions from localStorage on mount
  useEffect(() => {
    const loadTransactions = () => {
      try {
        const storedTransactions = localStorage.getItem(TRANSACTIONS_STORAGE_KEY);
        
        if (storedTransactions) {
          const parsedTransactions = JSON.parse(storedTransactions);
          
          if (Array.isArray(parsedTransactions)) {
            setTransactions(parsedTransactions);
            console.log(`Loaded ${parsedTransactions.length} transactions from storage`);
          }
        }
      } catch (error) {
        console.error('Failed to load transactions from localStorage:', error);
      }
      
      setIsInitialized(true);
    };
    
    loadTransactions();
  }, []);

  // Save transactions to localStorage whenever they change
  useEffect(() => {
    if (!isInitialized) return;
    
    try {
      localStorage.setItem(TRANSACTIONS_STORAGE_KEY, JSON.stringify(transactions));
    } catch (error) {
      console.error('Failed to save transactions to localStorage:', error);
    }
  }, [transactions, isInitialized]);

// Function to add a new transaction
const addTransaction = (transactionData) => {
    if (!transactionData || !transactionData.hash) {
      console.error('Invalid transaction data:', transactionData);
      return false;
    }

    // Create a complete transaction object with defaults for missing fields
    const newTransaction = {
      hash: transactionData.hash,
      type: transactionData.type || TRANSACTION_TYPES.UNKNOWN,
      from: transactionData.from || address || '',
      to: transactionData.to || '',
      value: transactionData.value || '0',
      timestamp: transactionData.timestamp || Date.now(),
      status: transactionData.status || 'pending',
      tokenSymbol: transactionData.tokenSymbol || (currentChain?.key === 'ethereum' ? 'ETH' : 'BROCK'),
      tokenDecimal: transactionData.tokenDecimal || 18,
      fee: transactionData.fee || '0',
      network: transactionData.network || currentChain?.key || 'unknown',
      data: transactionData.data || '',
      explorerUrl: transactionData.explorerUrl || (currentChain?.explorerUrl ? `${currentChain.explorerUrl}/tx/${transactionData.hash}` : '')
    };
    
    // Debug logging
    console.log('Adding transaction to history:', {
      hash: newTransaction.hash,
      type: newTransaction.type,
      network: newTransaction.network
    });

    // Check if transaction already exists
    const exists = transactions.some(tx => tx.hash === newTransaction.hash);
    
    if (exists) {
      // Update existing transaction
      setTransactions(prevTransactions => 
        prevTransactions.map(tx => 
          tx.hash === newTransaction.hash ? { ...tx, ...newTransaction } : tx
        )
      );
      return true;
    }

    // Add new transaction (at the beginning of the array for newest first)
    setTransactions(prevTransactions => [newTransaction, ...prevTransactions]);
    return true;
  };

  // Function to update a transaction's status
  const updateTransactionStatus = (hash, status) => {
    if (!hash) return false;
    
    setTransactions(prevTransactions => 
      prevTransactions.map(tx => 
        tx.hash === hash ? { ...tx, status, timestamp: Date.now() } : tx
      )
    );
    return true;
  };

  // Function to get transactions for the current address and network
  const getTransactions = (options = {}) => {
    const { 
      address: filterAddress = address, 
      network: filterNetwork = currentChain?.key,
      type: filterType = null,
      search: searchTerm = null,
      limit = 50,
      offset = 0
    } = options;

    let filtered = [...transactions];
    
    // Filter by address
    if (filterAddress) {
      const addressLower = filterAddress.toLowerCase();
      filtered = filtered.filter(tx => 
        (tx.from && tx.from.toLowerCase() === addressLower) || 
        (tx.to && tx.to.toLowerCase() === addressLower)
      );
    }
    
    // Filter by network
    if (filterNetwork) {
      filtered = filtered.filter(tx => tx.network === filterNetwork);
    }
    
    // Filter by type
    if (filterType) {
      filtered = filtered.filter(tx => tx.type === filterType);
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(tx => 
        (tx.hash && tx.hash.toLowerCase().includes(term)) || 
        (tx.from && tx.from.toLowerCase().includes(term)) || 
        (tx.to && tx.to.toLowerCase().includes(term)) ||
        (tx.tokenSymbol && tx.tokenSymbol.toLowerCase().includes(term))
      );
    }
    
    // Sort by timestamp (newest first)
    filtered.sort((a, b) => b.timestamp - a.timestamp);
    
    // Apply pagination
    return filtered.slice(offset, offset + limit);
  };

  // Function to clear all transactions
  const clearTransactions = () => {
    setTransactions([]);
    return true;
  };

  return (
    <TransactionContext.Provider
      value={{
        transactions,
        addTransaction,
        updateTransactionStatus,
        getTransactions,
        clearTransactions,
        TRANSACTION_TYPES
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
};